import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { loadDraft, saveDraft, clearDraft } from "../../lib/onboarding-storage";
import { langName } from "../../lib/languages";
import OnboardingLayout from "./OnboardingLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  Save,
  ArrowRight,
} from "lucide-react";

const TARGET_EXECUTIVES = [
  "CEO", "CFO", "COO", "CTO", "CHRO", "CMO", "CIO",
  "Managing Director", "General Manager", "Founder", "Board Member",
  "General Business Leader",
];
const COMPANY_SIZES = [
  "SMB (10-100 employees)",
  "Mid-market (100-1000 employees)",
  "Enterprise (1000+ employees)",
  "Any",
];

function SectionHeader({ label, hint }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-sans-ui uppercase tracking-[0.18em] text-accent/90">
        {label}
      </div>
      {hint && (
        <div className="mt-1 text-xs text-muted-foreground/80">{hint}</div>
      )}
    </div>
  );
}

export default function Step4Campaign() {
  const nav = useNavigate();
  const { user } = useAuth();

  const draft = loadDraft("step4") || {};
  const [name, setName] = useState(draft.name || "");
  const [country, setCountry] = useState(
    draft.country || user?.primary_country || ""
  );
  const [industry, setIndustry] = useState(
    draft.industry || user?.industries?.[0] || ""
  );
  const [businessFunction, setBusinessFunction] = useState(
    draft.businessFunction || user?.functions?.[0] || ""
  );
  const [targetExec, setTargetExec] = useState(draft.targetExec || "");
  const [challenge, setChallenge] = useState(draft.challenge || "");
  const [goal, setGoal] = useState(draft.goal || "");
  const [defaultLanguage, setDefaultLanguage] = useState(
    draft.defaultLanguage || user?.primary_language || "en"
  );
  const [companySize, setCompanySize] = useState(draft.companySize || "");
  const [description, setDescription] = useState(draft.description || "");
  const [allowSwitch, setAllowSwitch] = useState(!!draft.allowSwitch);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const supportedCountries = user?.countries || [];
  const supportedIndustries = user?.industries || [];
  const supportedFunctions = user?.functions || [];
  const supportedLanguages =
    user?.supported_languages || [user?.primary_language || "en"];

  useEffect(() => {
    saveDraft("step4", {
      name, country, industry, businessFunction, targetExec,
      challenge, goal, defaultLanguage, companySize, description, allowSwitch,
    });
  }, [
    name, country, industry, businessFunction, targetExec,
    challenge, goal, defaultLanguage, companySize, description, allowSwitch,
  ]);

  const canSubmit =
    name.trim() &&
    country &&
    industry &&
    businessFunction &&
    targetExec &&
    challenge.trim() &&
    goal.trim() &&
    defaultLanguage &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        country,
        industry,
        function: businessFunction,
        target_executive: targetExec,
        primary_business_challenge: challenge.trim(),
        goal: goal.trim(),
        default_language: defaultLanguage,
        company_size_focus: companySize || null,
        description: description.trim() || null,
        allow_language_switch: allowSwitch && supportedLanguages.length > 1,
        status: "active",
      };
      const { data } = await api.post("/consultants/me/share-links", payload);
      clearDraft("step4");
      nav(`/onboarding/campaign-live/${data.id}`);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Couldn't launch your campaign"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingLayout
      currentStep="campaign"
      eyebrow="Step 3 · Campaign"
      title="Design your first campaign"
      subtitle="Answer a few short prompts. The AI writes the landing page, Business Growth Discovery and outreach copy from your inputs — you can refine anything later."
      estimatedMinutes="~3 min"
    >
      <form
        onSubmit={onSubmit}
        className="space-y-10"
        data-testid="onboarding-step4-form"
      >
        {/* Naming */}
        <section>
          <SectionHeader
            label="Campaign name"
            hint="For your reference only — leaders never see it."
          />
          <Input
            data-testid="campaign-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. UAE CFO Digital Finance Pulse"
            autoFocus
          />
        </section>

        {/* Audience */}
        <section>
          <SectionHeader
            label="Audience"
            hint="Country, industry and function drive question selection and benchmarks."
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger
                  className="mt-2"
                  data-testid="campaign-country"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {supportedCountries.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger
                  className="mt-2"
                  data-testid="campaign-industry"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {supportedIndustries.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Function</Label>
              <Select
                value={businessFunction}
                onValueChange={setBusinessFunction}
              >
                <SelectTrigger
                  className="mt-2"
                  data-testid="campaign-function"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {supportedFunctions.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5">
            <Label className="text-sm">Target executive</Label>
            <Select value={targetExec} onValueChange={setTargetExec}>
              <SelectTrigger
                className="mt-2"
                data-testid="campaign-target-exec"
              >
                <SelectValue placeholder="Who you'll speak to" />
              </SelectTrigger>
              <SelectContent>
                {TARGET_EXECUTIVES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Message */}
        <section>
          <SectionHeader
            label="Your angle"
            hint="These two answers shape the entire campaign narrative."
          />
          <div className="space-y-5">
            <div>
              <Label className="text-sm">Primary business challenge</Label>
              <Textarea
                data-testid="campaign-challenge"
                rows={2}
                value={challenge}
                onChange={(e) => setChallenge(e.target.value)}
                className="mt-2"
                placeholder="e.g. Slow monthly close and manual reconciliation drag on decision speed."
              />
            </div>
            <div>
              <Label className="text-sm">Campaign goal</Label>
              <Textarea
                data-testid="campaign-goal"
                rows={2}
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="mt-2"
                placeholder="e.g. Book 10 discovery calls with mid-market CFOs this quarter."
              />
            </div>
          </div>
        </section>

        {/* Language */}
        <section>
          <SectionHeader label="Language" />
          <Label className="text-sm">Default language</Label>
          <Select
            value={defaultLanguage}
            onValueChange={setDefaultLanguage}
          >
            <SelectTrigger className="mt-2" data-testid="campaign-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {supportedLanguages.map((c) => (
                <SelectItem key={c} value={c}>
                  {langName(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            Only your registered languages appear here. Add more in Profile → Practice.
          </p>
        </section>

        {/* Advanced */}
        <section>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
            data-testid="campaign-advanced-toggle"
          >
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            Advanced settings
          </button>

          {showAdvanced && (
            <div
              className="mt-4 space-y-5 rounded-lg border border-border/60 bg-card/40 p-5"
              data-testid="campaign-advanced-panel"
            >
              <div>
                <Label className="text-sm">Company size focus</Label>
                <Select value={companySize} onValueChange={setCompanySize}>
                  <SelectTrigger
                    className="mt-2"
                    data-testid="campaign-company-size"
                  >
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Campaign description</Label>
                <Textarea
                  data-testid="campaign-description"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-2"
                  placeholder="Extra context for the AI (audience nuance, tone, etc.)"
                />
              </div>
              {supportedLanguages.length > 1 && (
                <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-3">
                  <div>
                    <div className="font-sans-ui text-sm">
                      Allow visitors to switch language
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Prospects can pick from{" "}
                      {supportedLanguages.map(langName).join(" · ")}.
                    </div>
                  </div>
                  <Switch
                    checked={allowSwitch}
                    onCheckedChange={setAllowSwitch}
                    data-testid="campaign-allow-lang-switch"
                  />
                </div>
              )}
            </div>
          )}
        </section>

        <div className="flex items-center justify-between pt-4">
          <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Save className="w-3 h-3" /> Draft autosaved
          </span>
          <div className="flex flex-col items-end gap-1">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              data-testid="campaign-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Launch campaign
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-xs text-muted-foreground/80">
              You&apos;ll get a shareable link + QR next
            </span>
          </div>
        </div>
      </form>
    </OnboardingLayout>
  );
}
